import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TimePickerService } from '../../providers/time-picker/time-picker.service';
import { HttpClient } from '@angular/common/http';
import { ITriageItem, ITriageObject } from '../../models/types';

interface NextUrl {
  pageName?: string;
  dashboard?: string;
  queryParams?: {}; // eslint-disable-line
}

@Injectable({
  providedIn: 'root'
})
export class ExternalURLService {
  anomalyToPage = {
    bytecount2to1: 'byte-count-anomaly',
    bytecount: 'byte-count-anomaly',
    mosaudioaverage: 'vcqav',
    mosvideoaverage: 'vcqav',
    callsetupduration: 'vcsdav',
    appdelayin: 'triage',
    appdelayout: 'triage',
    networkdelay: 'triage',
    packetcount2to1: 'packet-count-anomaly',
    packetcount1to2: 'packet-count-anomaly',
    packetcount: 'packet-count-anomaly',
    // in case kpi name does not match anything from above, we shall redirect the user to this page
    default: 'activetest-anomaly-drilldown'
  };

  datetimeExtremes: { min: number; max: number };
  activateRoute: ActivatedRoute;
  router: Router;

  constructor(
    activateRoute: ActivatedRoute,
    router: Router,
    private readonly timePickerService: TimePickerService,
    readonly httpClient: HttpClient
  ) {
    this.activateRoute = activateRoute;
    this.router = router;
  }

  hasRecognizableParams(): boolean {
    const snapshot = this.activateRoute.snapshot;
    const returnUrl = snapshot.queryParams ? snapshot.queryParams['returnUrl'] : null;
    // by now, navigation logic should have hidden 'reqFrom' behind returnUrl, so no need to check for this parameter
    return returnUrl ? true : false;
  }

  async redirectBasedOnURL(): Promise<void> {
    const snapshot = this.activateRoute.snapshot;
    const returnUrl = snapshot.queryParams ? snapshot.queryParams['returnUrl'] : null;
    if (!returnUrl) {
      this.launchDefaultView();
      return;
    }
    const navigationParams = this.splitReturnUrl(returnUrl);
    if (navigationParams['reqFrom'] === 'emailNotification') {
      const capturedParams = await this.buildUrlFromHandMadeParams(navigationParams);
      const nextUrl = this.buildUrl(capturedParams);
      if (nextUrl) {
        this.router.navigateByUrl(nextUrl);
      } else {
        // something was very wrong with the url. go to default view
        this.launchDefaultView();
      }
    } else {
      // we assume the returnURL contains a full fledged URL that does not need parsing/recognition
      this.router.navigateByUrl(returnUrl);
    }
  }

  buildUrl(nextUrl: NextUrl): string {
    if (!nextUrl.pageName && !nextUrl.dashboard) {
      return null;
    }
    const dashboard = nextUrl.dashboard ? nextUrl.dashboard : 'blank';
    let finalUrl = `/perspective/dfvhu/(dashboard:${dashboard})?`;
    if (nextUrl.queryParams) {
      finalUrl += this.presentAsUrlParams(nextUrl.queryParams);
    }
    if (dashboard !== 'blank') {
      finalUrl += '#dashboard';
    }
    return finalUrl;
  }

  async buildUrlFromHandMadeParams(navigationParams = {}): Promise<NextUrl> {
    const result: NextUrl = {};
    this.recognizePage(navigationParams, result);
    this.recognizeTime(navigationParams, result);
    if (result.pageName && result.pageName === 'triage') {
      this.recognizeTriage(navigationParams, result);
    } else {
      await this.recognizeAnomaly(navigationParams, result);
    }
    return result;
  }

  // eslint-disable-next-line
  recognizeTriage(navigationParams: any, nextUrl: NextUrl): void {
    try {
      nextUrl.pageName = undefined;
      nextUrl.dashboard = 'triage';

      const anomaly = JSON.parse(navigationParams['anomaly']);
      if (!anomaly) {
        return;
      }

      const item: ITriageItem = {
        kpiName: anomaly.kpiName,
        dimensions: anomaly.dimensions
      };

      if (anomaly.dimensionSiteId) {
        item.dimensionSiteId = anomaly.dimensionSiteId;
      }

      if (anomaly.dimensionAppId && anomaly.dimensionAppId !== 0) {
        item.dimensionAppId = anomaly.dimensionAppId;
      }

      const triageObj: ITriageObject = {
        isAnomaly: true,
        item: item
      };
      nextUrl.queryParams = { ...nextUrl.queryParams, triage_clicked: JSON.stringify(triageObj) };
    } catch (e) {
      nextUrl.pageName = undefined;
      return;
    }
  }

  // eslint-disable-next-line
  async recognizeAnomaly(navigationParams: any, nextUrl: NextUrl): Promise<any> {
    const anomalyProps = navigationParams['anomaly'];
    if (!anomalyProps) {
      return;
    }
    const resolvedAnomaly = new Promise(resolve => {
      const queryParams = {
        ...JSON.parse(anomalyProps),
        startTime: navigationParams['startTime'],
        endTime: navigationParams['endTime']
      };
      this.httpClient.post('/anomaly/v1/singleanomaly', queryParams).subscribe(
        response => {
          if (response) {
            nextUrl.queryParams = {
              ...nextUrl.queryParams,
              anomaly_clicked: JSON.stringify(response)
            };
          }
          resolve(null);
        },
        // eslint-disable-next-line
        error => {
          // kill pageName to indicate redirect to requested page not possible
          nextUrl.dashboard = undefined;
          resolve(null);
        }
      );
    });
    return resolvedAnomaly;
  }

  // eslint-disable-next-line
  recognizeTime(navigationParams: any, nextUrl: NextUrl): void {
    const startTime = Number(navigationParams['startTime']);
    const endTime = Number(navigationParams['endTime']);
    if (startTime && endTime) {
      const fixedTimeRange = this.timePickerService.makeFixedTimeRange(startTime, endTime);
      nextUrl.queryParams = { ...nextUrl.queryParams, time_range: JSON.stringify(fixedTimeRange) };
    }
  }

  // eslint-disable-next-line
  recognizePage(navigationParams: any, nextUrl: NextUrl): void {
    const view = navigationParams['view'];
    const viewSubtype = navigationParams['type'];
    const anomaly = navigationParams['anomaly'];
    switch (view) {
      case 'threatsView':
        {
          switch (viewSubtype) {
            case 'syn':
              nextUrl.pageName = 'syn-forensics-threats';
              break;
          }
        }
        break;
      case 'alertsView':
        {
          nextUrl.pageName = 'aiid3a';
          const anomalyType = viewSubtype !== 'activeTest' ? 'perf' : 'activeTest';
          nextUrl.queryParams = { ...nextUrl.queryParams, defaultTab: anomalyType };
        }
        break;
      case 'anomalyDetailsView':
        {
          const parsedAnomaly = JSON.parse(anomaly ? anomaly : '{}');
          nextUrl.dashboard = this.anomalyToPage[parsedAnomaly.kpiName];
          if (!nextUrl.dashboard) {
            // assume this is active test anomaly
            nextUrl.dashboard = this.anomalyToPage['default'];
          }
        }
        break;
    }
  }

  presentAsUrlParams(queryParams = {}): string {
    const pairs = [];
    for (const key of Object.keys(queryParams)) {
      const encodedVal = encodeURIComponent(queryParams[key]);
      // I have no use case to url-encode the value part. If you run into such case, please do so.
      pairs.push(`${key}=${encodedVal}`);
    }
    return pairs.join('&');
  }

  // eslint-disable-next-line
  splitReturnUrl(returnUrl = ''): any {
    const result = {};
    // no need to decode - that should have been done by angular upon parse of URL
    const rawParams = returnUrl.split('&');
    for (const p of rawParams) {
      const keyValuePair = p.split('=');
      result[keyValuePair[0]] = keyValuePair[1];
    }
    return result;
  }

  launchDefaultView(): void {
    this.router.navigate(['landing-page']);
  }
}
